<script setup>
import * as yup from 'yup';
import { computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import { __, request, getSelfcareAccountLogin } from '@/utils';
import Modal from "@components/modal.vue";
import StepAccount from '@components/loginForm/parts/step-account.vue';
import StepOtp from '@components/loginForm/parts/step-otp.vue';

const config = [
    {
        name: 'ACCOUNT',
        validation: yup.object({
            personal_id: yup.string().min(9, __('A valid Israeli ID number must be entered, including a check digit')).required(__('A valid Israeli ID number must be entered, including a check digit')),
        })
    },
    {
        name: 'PHONE_OTP',
        validation: yup.object({
            sms_validation: yup.string().min(4, __('must be at least 4 characters')).required(__('Required field')),
        })
    }
]

// ENUM for login steps
const STEPS = config.reduce((acc, item, index) => {
    acc[item.name] = index;
    return acc;
}, {});

defineProps(['isActive']);

const emit = defineEmits(['close', 'success', 'error']);

const activeStep = ref(STEPS['ACCOUNT']);
const loading = ref(false);
const submitType = ref('');
const isOTPCodeSend = ref(false);
const otpResponse = ref(null);
const callType = ref('');
const sendOTP = ref(true);
const confirmOTP = ref(false)
const userName = ref('');
const invalidOTP = ref(false);

const schema = config.map(item => item.validation);
const activeSchema = computed(() => schema[activeStep.value]);

const { handleSubmit, meta, values } = useForm({
    validationSchema: activeSchema,
    keepValuesOnUnmount: true
});

const submitText = computed(() => {
    switch(activeStep.value) {
        case STEPS['ACCOUNT']:
            return __('Send a code to your mobile');
        case STEPS['PHONE_OTP']:
            return __('Confirmation and login');
    }
});

const handlePostRequest = async (body, callback) => {
    const [data, error] = await request(ajax_object.ajax_url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
    })
    loading.value = false;
    let responseData = {};
    if(data?.data) {
        responseData = JSON.parse(data.data);
    }

    if(responseData?.response?.status) {
        otpResponse.value = responseData.response;
        callback(responseData);
        sendOTP.value = false;
        confirmOTP.value = true;
        userName.value = responseData?.response?.account_name;
    } 
    
    if(error || !responseData?.response?.status) {
        emit('error');
    }
}

const onSubmit = handleSubmit(async (values) => {
    loading.value = true;

    values['type'] = callType.value;

    if (submitType.value === 'msisdn' && !isOTPCodeSend.value) {
        values['type'] = 'msisdn';
        handlePostRequest(
            `action=selfcare_send_otp_code&personal_id=${values.personal_id}&type=msisdn`,
            () => activeStep.value = STEPS['PHONE_OTP']
        );
        callType.value = 'msisdn';
        isOTPCodeSend.value = true;
    } else if (submitType.value === 'email' && !isOTPCodeSend.value) {
        handlePostRequest(
            `action=selfcare_send_otp_code&personal_id=${values.personal_id}&type=email`,
            () => activeStep.value = STEPS['PHONE_OTP']
        );
        callType.value = 'email';
        isOTPCodeSend.value = true;
    }

    switch(values.step) {
        case 'account':
            activeStep.value = STEPS['ACCOUNT'];
            loading.value = false;
            break;
        case 'success':
            delete values.step;
            try {
                const response = await getSelfcareAccountLogin({ ...values });
                const responseData = JSON.parse(response.data);
   
                if(responseData.response.error === "otp_code_not_valid") {
                    invalidOTP.value = true;
                    loading.value = false;
                    return;
                }
                
                if(responseData.response.status) {
                    emit('success');
                } else {
                    emit('error');
                }
                invalidOTP.value = false;
            } catch (error) {
                emit('error');
                console.log(error)
            }
            break;
    }
});

const setSubmitType = (type) => {
    submitType.value = type;
    callType.value = type;
};

const isVisible = (values) => {
    const enumValues = values.map(item => STEPS[item]);
    return enumValues.includes(activeStep.value);
};


const OTPTitle = computed(() => {
    if (otpResponse.value?.display && callType.value === 'msisdn') {
        return `${__('An SMS with a verification code will be sent to the number')} - <span dir="ltr" style="unicode-bidi: bidi-override;">${otpResponse.value.display}</span>.</br> ${__('Please enter the 4-digit code you received')}.`;
    }
    if (otpResponse.value?.display && callType.value === 'email') {
        return `${__('A verification code will now be sent to your email')} - <span dir="ltr" style="unicode-bidi: bidi-override;">${otpResponse.value.display}</span>.</br> ${__('Please enter the 4-digit code you received')}.`;
    }

    return '';
});

const linkIsDisabled = computed(() => isVisible(['PHONE_OTP']) ? loading.value : !meta.value.valid || loading.value);

const handleLinkClick = async (type) => {
    submitType.value = type;
    callType.value = type;

    if(!isVisible(['PHONE_OTP'])) {
        return
    }

    handlePostRequest(`action=selfcare_send_otp_code&personal_id=${values.personal_id}&type=${type}`);
}
</script>

<template>
    <Modal :isActive="isActive" @close="$emit('close')" class="popup--login login-form">
        <template #head>
            <h6>{{ __('Login') }}</h6>
        </template>
        <template #body>
            <form @submit="onSubmit">
                <input type="hidden" name="submitType" :value="submitType" />
                <StepAccount
                    v-if="isVisible(['ACCOUNT', 'PHONE_OTP'])"
                    :success="isVisible(['PHONE_OTP'])"
                    :name="userName"
                />
                <StepOtp 
                    v-if="isVisible(['PHONE_OTP'])" 
                    :title="OTPTitle"
                    :personal-id="values.personal_id"
                    :otp-type="callType"
                    :class="{'invalid-otp': invalidOTP}"
                >
                    <template #error v-if="invalidOTP">
                        <p class="red-text">{{ __('You entered an incorrect code, please try again.') }}</p>
                    </template>
                </StepOtp>

                <div class="confirm-btn-holder" v-if="confirmOTP">
                    <button type="submit" class="btn w160 mb14" :disabled="!meta.valid || loading">
                        {{ submitText }}
                    </button>
                </div>

                <div class="confirm-btn-holder"  v-if="sendOTP">
                    <button type="submit" class="btn w160 mb14" @click="setSubmitType('msisdn')" :disabled="!meta.valid || loading">
                        {{ submitText }}
                    </button>
                </div>

                <div class="confirm-btn-holder confirm-btn-holder-top-spacing" v-if="sendOTP || submitType === 'msisdn'">
                    <button type="submit" class="btn--link w160 mb14 link-underline__regular" @click="handleLinkClick('email')" :disabled="linkIsDisabled">
                        {{ __('Send me the code to my email') }}
                    </button>
                </div>

                <div class="confirm-btn-holder confirm-btn-holder-top-spacing" v-if="isVisible(['PHONE_OTP']) && submitType === 'email'">
                    <button type="submit" class="btn--link w160 mb14 link-underline__regular" @click="handleLinkClick('msisdn')" :disabled="linkIsDisabled">
                        {{ __('Send me the code to my mobile') }}
                    </button>
                </div>
            </form>
        </template>
    </Modal>
</template>